// 花嫁の描き直し3件 (2026-10-23 に比較版)。?brideRedraw=floor|exec|oldhug|hugback|oldexec (開発版だけ・カンマで並べる・all/1 で floor と exec)。
// 10-27 検収 kensyu-bride-1023b の答え H-1「抱きしめて見える」・H-2「配布版に」→ 抱擁は hugfront (両腕で抱き、腕と袖を主人公の前へ) が配布版。
//   開発版だけ ?brideRedraw=oldhug で前の配布版 (腕を開いたまま)、hugback で両腕で抱くが腕は主人公の後ろ (10-23 の hug)。
//
// 作者の答え (答えるページ FQL5vcJJCGAp5TyjKwXjz3・2026-10-23)：
//   問6 a = 抱擁は両腕で抱く／問7 a = 腕を伸ばしたまま滑って来て、着いた所で抱く
//   問8 a = 回る技の裾は床に着ける・回る途中の絵も含めて全部描き直す
//   問10 = 絵の決まり (docs/art-style.md) に従う → 介錯の固有の絵 exec_*_1009 も描き直す
// 作り：
//   hug   = 誓いの抱擁 (vowEmbrace) の当たる瞬間までは腕を開いたまま (embrace_open) 滑り、当たりの途中で
//           両腕で抱く絵 (embrace_hug_1023) に替える。技の数値・当たりは変えない。
//   hugfront = hug に加えて、抱く絵の腕と袖だけを切り出した絵 (embrace_hug_1023_front) を主人公より手前に重ねる。
//           10-23 検収 H-1「レイヤーがつかまってるような感じで、抱きしめて欲しい…ボスを手前側に書いた方がいいのかな」。
//           体と裾は主人公の後ろのまま、腕だけが主人公の前に来るので、前から包んで見える。腕の切り出し＝docs/research/2026-10-23-bride-embrace-floor-exec.md
//   floor = 剣舞の前向きの4枚と回る途中の3枚を、裾が床に着いた描き直しへ替える (sprites.ts の差し替え + bride_spin_fx.ts)。
//   exec  = 固有の致命の4枚 (exec_found/glide/coil/okaeri_1009) を描き直した _1023 へ替える。
//           10-23 検収の答え E-2「新しい4枚にする」で配布版の既定にした。前の4枚は開発版 ?brideRedraw=oldexec。
//   大きさ・横の位置は sprites.ts の POSE_SCALE_FIX.bride と BRIDE_REDRAW_ANCHOR_X (描き直しの名前で引く)。
// 配布版は URL を読まない (import.meta.env.DEV の時だけ入れる)。とどめの4枚の差し替えだけは読み込んだ時に配布版でも入れる。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs)。
// 記録 = docs/research/2026-10-23-bride-embrace-floor-exec.md

import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { bossSpriteFileStem, clearBossSpriteCache, setSpriteFileOverride } from './sprites'
import { setBrideTurnArtFloor } from './bride_spin_fx'
import type { BossSpritePlacement } from './sprites'
import type { SpriteKey } from './types'

export type BrideRedrawPart = 'hug' | 'hugfront' | 'floor' | 'exec' | 'oldexec'
/** 配布版の既定 (10-27 から抱擁は hugfront)。 */
const SHIPPED_PARTS: readonly BrideRedrawPart[] = ['hug', 'hugfront']

export const BRIDE_HUG_TIMELINE_1023: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle' },
  { phase: 'telegraph', at: 0.30, image: 'embrace_open', hold: true, tag: 'anticipation' },
  // 腕を開いたまま滑って来る (当たりの前半)。
  { phase: 'active', at: 0.0, image: 'embrace_open', hold: true },
  // 着いた所で両腕で抱く。
  { phase: 'active', at: 0.6, image: 'embrace_hug_1023', hold: true, tag: 'contact' },
  { phase: 'recovery', at: 0.0, image: 'embrace_hug_1023', tag: 'followThrough' },
]

// 絵のキーは SpriteKey の並びに無い名前 (追加ポーズ・固有の致命の絵) なので文字列で持ち、渡す時だけ型を合わせる。
export const BRIDE_FLOOR_KEYS: readonly string[] = [
  'flurry_swing_right', 'flurry_swing_cross', 'flurry_swing_left', 'flurry_slam_windup',
]
export const BRIDE_EXEC_REDRAW_KEYS: readonly string[] = [
  'exec_found_1009', 'exec_glide_1009', 'exec_coil_1009', 'exec_okaeri_1009',
]

/** URL の値を部品の集まりへ。既定 (配布版・開発版で指定なし) は hug と hugfront。開発版でなければ常に既定。 */
export function parseBrideRedraw(raw: string | null, dev: boolean = import.meta.env.DEV): Set<BrideRedrawPart> {
  const out = new Set<BrideRedrawPart>(SHIPPED_PARTS)
  if (!dev || !raw) return out
  for (const t of raw.split(',').map((s) => s.trim())) {
    if (t === '1' || t === 'all') { out.add('floor'); out.add('exec') }
    else if (t === 'oldhug') { out.delete('hug'); out.delete('hugfront') }
    else if (t === 'hugback') out.delete('hugfront')
    else if (t === 'floor' || t === 'exec' || t === 'oldexec') out.add(t)
  }
  return out
}

/** とどめの4枚: 既定 (配布版) は描き直した _1023、false で前の _1009。 */
function setBrideExecArt(redrawn: boolean): void {
  for (const k of BRIDE_EXEC_REDRAW_KEYS) setSpriteFileOverride('bride', '', k as SpriteKey, redrawn ? k.replace('_1009', '_1023') : null)
}
setBrideExecArt(true)

let original: BossAttackTimeline | null = null
let parts = new Set<BrideRedrawPart>()
export function brideRedrawParts(): ReadonlySet<BrideRedrawPart> { return parts }

export function setBrideRedraw(raw: string | null, dev: boolean = import.meta.env.DEV): void {
  parts = parseBrideRedraw(raw, dev)
  const tl = BOSS_ATTACK_TIMELINES['bride']!
  original ??= tl.vowEmbrace!
  tl.vowEmbrace = parts.has('hug') ? BRIDE_HUG_TIMELINE_1023 : original
  for (const k of BRIDE_FLOOR_KEYS) setSpriteFileOverride('bride', '', k as SpriteKey, parts.has('floor') ? `${k}_floor_1023` : null)
  setBrideExecArt(!parts.has('oldexec'))
  setBrideTurnArtFloor(parts.has('floor'))
  clearBossSpriteCache('bride')
  if (parts.has('hugfront')) hugFrontImage() // 最初の抱擁で腕が1コマ遅れないよう先に読む
}

// ───────── 抱く腕を主人公の前へ (hugfront) ─────────
const HUG_KEY = 'embrace_hug_1023'
// 腕の絵は、抱く絵に今かかっている差し替え (案A の線 *_art_1025_a・src/bride_art_sample_1025.ts) と同じ幹の _front を読む。
const hugFrontImgs = new Map<string, HTMLImageElement>()
function hugFrontImage(): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  const stem = bossSpriteFileStem('bride', HUG_KEY)
  let img = hugFrontImgs.get(stem)
  if (!img) { img = new Image(); img.src = `/sprites/bosses/bride/${stem}_front.webp`; hugFrontImgs.set(stem, img) }
  return img.complete && img.naturalWidth > 0 ? img : null
}
/** 抱く絵の差し替えを替えた後に呼ぶ (最初の抱擁で腕が1コマ遅れないよう先に読む)。 */
export function preloadBrideHugFront(): void { if (parts.has('hugfront')) hugFrontImage() }
/** 主人公を描いた後に呼ぶ。花嫁が抱く絵を描いた時だけ、同じ置き場所・向きで腕と袖を重ねる。 */
export function drawBrideHugFront(ctx: CanvasRenderingContext2D, bossId: string | undefined, pl: BossSpritePlacement | null): void {
  if (!parts.has('hugfront') || bossId !== 'bride' || !pl || !pl.imageKey.includes(HUG_KEY)) return
  const img = hugFrontImage()
  if (!img) return
  const r = pl.imageRect
  ctx.save()
  ctx.translate(pl.pivot.x, pl.pivot.y)
  ctx.scale(pl.facingMul, 1)
  if (pl.rotationRad !== 0) ctx.rotate(pl.rotationRad)
  ctx.translate(-pl.pivot.x, -pl.pivot.y)
  ctx.drawImage(img, r.x, r.y, r.width, r.height)
  ctx.restore()
}

// 読み込んだ時点で配布版の並びにする (setBrideRedraw を呼ばない入口でも両腕の抱擁が入るように)。
setBrideRedraw(null, false)
